"use client"

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react"

/**
 * A starter prompt to pre-seed the panel with when it opens — a chip or CTA that
 * both opens chat AND sends a first message. `promptKey` is stable provenance
 * (stamped host-side as the trace `prompt_key`). Omit the seed entirely (a plain
 * `requestPanelOpen()`) to just open the panel with no message.
 */
export interface PromptSeed {
  prompt: string
  promptKey?: string
}

interface ChatLauncherContextValue {
  pageInstanceActive: boolean
  setPageInstanceActive: (v: boolean) => void
  /**
   * Monotonic counter of "open the panel now" requests from an AI-answer
   * surface (the search/hero lane, the test-detail section) or a starter-prompt
   * chip. The mounted `ResolvedChatWidget` watches it and opens live.
   *
   * A counter rather than a boolean: two escalations in one page view must both
   * open the panel, and a boolean that's already `true` emits no change.
   */
  openRequests: number
  /**
   * The seed carried by the most recent `requestPanelOpen`, or `undefined` when
   * it was a plain open. Coupled to `openRequests` (bumped together) so the
   * widget reads the right seed for the current request. Read only when
   * `openRequests` changes.
   */
  pendingSeed: PromptSeed | undefined
  /** Ask the mounted chat panel to open on THIS page view, optionally seeded. */
  requestPanelOpen: (seed?: PromptSeed) => void
}

const ChatLauncherContext = createContext<ChatLauncherContextValue>({
  pageInstanceActive: false,
  setPageInstanceActive: () => {},
  openRequests: 0,
  pendingSeed: undefined,
  requestPanelOpen: () => {},
})

export function ChatLauncherProvider({ children }: { children: ReactNode }) {
  const [pageInstanceActive, setPageInstanceActive] = useState(false)
  // Counter + seed in one atomic state so the widget's open effect always reads
  // the seed that belongs to the request it's reacting to — a separate seed
  // state could tear if two requests land in different renders.
  const [openRequest, setOpenRequest] = useState<{
    count: number
    seed: PromptSeed | undefined
  }>({ count: 0, seed: undefined })
  const requestPanelOpen = useCallback(
    (seed?: PromptSeed) =>
      setOpenRequest((prev) => ({ count: prev.count + 1, seed })),
    [setOpenRequest]
  )
  return (
    <ChatLauncherContext.Provider
      value={{
        pageInstanceActive,
        setPageInstanceActive,
        openRequests: openRequest.count,
        pendingSeed: openRequest.seed,
        requestPanelOpen,
      }}
    >
      {children}
    </ChatLauncherContext.Provider>
  )
}

export function useChatLauncherContext() {
  return useContext(ChatLauncherContext)
}

export function usePageScopedChat(active: boolean): void {
  const { setPageInstanceActive } = useChatLauncherContext()
  useEffect(() => {
    if (active) setPageInstanceActive(true)
    return () => {
      if (active) setPageInstanceActive(false)
    }
  }, [active, setPageInstanceActive])
}
